import { useEffect, useState } from 'react'
import { type EditPhotoWithCropFormState } from '../model/types'
import { usePhotoById, usePhotoSocialById } from '@/entities/Photos'
import { errorHandler } from '@/shared/lib/errorHandler'
import { usePhotoCropByIdApi, usePhotoSocialCropByIdApi } from '../api/edit-photo-with-crop-api'
import { editPhotoWithCropFormDefault } from './consts'
import { FormikConfig } from 'formik'

export const useEditPhotoWithCrop = (
  selectedPhotoId: number,
  queryKeyForObjectUpdate: string,
  queryKeyForPageUpdate: string,
  onCloseModal: () => void,
  isSocialCrop: boolean,
) => {
  const [initialValues, setInitialValues] = useState<EditPhotoWithCropFormState>(
    () => editPhotoWithCropFormDefault,
  )
  const [cropValue, setCropValue] = useState({
    centerOffsetX: 0,
    centerOffsetY: 0,
    zoom: 1,
  })

  const {
    data: currentPhoto,
    isLoading: isLoadingPhoto,
    isError: isErrorPhoto,
  } = isSocialCrop ? usePhotoSocialById(selectedPhotoId) : usePhotoById(selectedPhotoId)

  useEffect(() => {
    if (currentPhoto) {
      setInitialValues(() => ({
        id: currentPhoto.id,
        centerOffsetX: null,
        centerOffsetY: null,
        zoom: 0,
        tag: currentPhoto.tag,
      }))
    }
  }, [currentPhoto])

  const {
    mutateAsync,
    isPending: isPendingUpdate,
    isError: isErrorUpdate,
  } = usePhotoCropByIdApi(queryKeyForObjectUpdate, selectedPhotoId, queryKeyForPageUpdate)

  const {
    mutateAsync: mutateSocialAsync,
    isPending: isPendingSocialUpdate,
    isError: isErrorSocialUpdate,
  } = usePhotoSocialCropByIdApi(queryKeyForObjectUpdate, selectedPhotoId, queryKeyForPageUpdate)

  const submitForm: FormikConfig<EditPhotoWithCropFormState>['onSubmit'] = async values => {
    try {
      const { tag } = values
      const state: EditPhotoWithCropFormState = {
        id: selectedPhotoId,
        tag,
        ...cropValue,
      }
      await mutateAsync(state)
      onCloseModal()
    } catch (error) {
      errorHandler(error)
    }
  }

  const submitFormSocial: FormikConfig<EditPhotoWithCropFormState>['onSubmit'] = async () => {
    try {
      const state: EditPhotoWithCropFormState = {
        id: selectedPhotoId,
        tag: 8,
        ...cropValue,
      }
      await mutateSocialAsync(state)
      onCloseModal()
    } catch (error) {
      errorHandler(error)
    }
  }

  return {
    cropValue,
    currentPhoto,
    isLoadingPhoto,
    isErrorPhoto,
    isPendingUpdate: isSocialCrop ? isPendingSocialUpdate : isPendingUpdate,
    isErrorUpdate: isSocialCrop ? isErrorSocialUpdate : isErrorUpdate,
    initialValues,
    submitForm: isSocialCrop ? submitFormSocial : submitForm,
    setCropValue,
  }
}
